/**
 * FIN-12167: Threshold-Combinations display helpers.
 * - parseCombinationThresholds: tolerate array or JSON-string `thresholds`.
 * - formatThresholdValue: round a threshold value for display (<=2 decimals,
 *   trailing zeros stripped) so a raw recommended float like 20.709115895556575
 *   renders as 20.71 instead of 15 decimals. Non-numeric values pass through.
 * - sortCombinationsByThreshold: order combinations by the numeric threshold value
 *   ascending (not targetRank), so the recommended value lands in its natural place
 *   instead of last. Non-mutating.
 */
export const parseCombinationThresholds = (thresholds) => {
  if (Array.isArray(thresholds)) return thresholds;
  if (typeof thresholds === 'string') {
    try {
      return JSON.parse(thresholds);
    } catch {
      return [];
    }
  }
  return [];
};

export const formatThresholdValue = (value) => {
  const num =
    typeof value === 'number'
      ? value
      : typeof value === 'string' &&
        value.trim() !== '' &&
        !Number.isNaN(Number(value))
      ? Number(value)
      : null;
  if (num === null || Number.isNaN(num)) return value;
  return parseFloat(num.toFixed(2));
};

/**
 * FIN-12167 (#1) — canonical computation-method labels. The 4 supported methods are defined
 * backend-side in AutoThresholdConfigService.ValidMethods (mean, mean_sd, mean_2sd, mean_extremum)
 * and surfaced in the config UI (ThresholdGenerationBanner/AutoThresholdConfigModal METHOD_OPTIONS);
 * these compact labels mirror that naming for the threshold-box header. Keep in sync with both.
 */
export const THRESHOLD_METHOD_LABELS = {
  mean: 'Mean (μ)',
  mean_sd: 'Mean ± SD',
  mean_2sd: 'Mean ± 2SD',
  mean_extremum: 'Mean-Max / Mean-Min'
};

/**
 * FIN-12167 (#1) — the generation method for a single threshold. Each threshold in the
 * auto_threshold_result `thresholds` payload carries `percentile` (e.g. 0.5, 0.95, 0.99) and/or
 * `method` (one of THRESHOLD_METHOD_LABELS' keys). A percentile renders as p<pct> (0.5 -> p50,
 * 0.99 -> p99); a named method renders via THRESHOLD_METHOD_LABELS, with an unknown/future method
 * falling back to a capitalised form so it still shows something meaningful. '' when neither present.
 */
export const thresholdMethodLabel = (threshold) => {
  if (!threshold) return '';
  const { percentile, method } = threshold;
  if (
    percentile != null &&
    percentile !== '' &&
    !Number.isNaN(Number(percentile))
  ) {
    return `p${Math.round(Number(percentile) * 100)}`;
  }
  if (method != null && String(method).trim() !== '') {
    const m = String(method).trim().toLowerCase();
    if (THRESHOLD_METHOD_LABELS[m]) return THRESHOLD_METHOD_LABELS[m];
    // Unknown/future method — capitalise words split on _ so it's still readable.
    return m
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return '';
};

/**
 * The method labels for one condition, one per tag. FIN-12908's merged_labels (several
 * percentiles collapsing to one value) are listed separately ("p50", "p75") rather than as an
 * equality chain, which read as a broken formula (FIN-13987).
 */
export const conditionMethodTags = (t) =>
  Array.isArray(t.merged_labels) && t.merged_labels.length > 1
    ? t.merged_labels.map((l) => THRESHOLD_METHOD_LABELS[l] || l)
    : [thresholdMethodLabel(t)].filter(Boolean);

/**
 * FIN-12909 — sort key over the VARYING threshold. The old first-threshold key sorted every
 * combination by the fixed condition's identical value (effectively unsorted). Sum across the
 * per-field values keeps single-condition behaviour identical and orders multi-condition combos
 * by their varying component.
 */
const combinationSortValue = (combo) => {
  const thresholds = parseCombinationThresholds(combo && combo.thresholds);
  if (!thresholds.length) return Infinity;
  const nums = thresholds
    .map((t) => Number(t.value))
    .filter((n) => !Number.isNaN(n));
  return nums.length ? nums.reduce((a, b) => a + b, 0) : Infinity;
};

export const sortCombinationsByThreshold = (combinations) =>
  [...(combinations || [])].sort(
    (a, b) => combinationSortValue(a) - combinationSortValue(b)
  );

/**
 * FIN-13987 — the conditions of a thresholds list keyed by identity: field + operator, plus the
 * occurrence for a field bounded twice (x >= 1 AND x <= 9). Never match conditions by position:
 * the analyser drops a condition with no candidate values from every combination but keeps it in
 * the generation-time list (current_thresholds).
 */
export const keyedConditions = (thresholds) => {
  const seen = {};
  return parseCombinationThresholds(thresholds).map((threshold) => {
    const label = `${threshold.field} ${threshold.operator}`;
    seen[label] = (seen[label] || 0) + 1;
    return { key: `${label} #${seen[label]}`, label, threshold };
  });
};

/**
 * FIN-13987 — one group per RESULT (flagged count). The analyser crosses every candidate of
 * every condition, so a multi-condition rule produced a card per combination and the list ran
 * long; many combinations raise the same number of alerts. Groups closest to the target alert
 * rate come first; runs generated before the target became required have none, so most alerts
 * first. `differs` lists the keyedConditions keys whose value is not the same across the group.
 */
export const groupCombinationsByResult = (
  combinations,
  targetAlertRate = null
) => {
  const byCount = new Map();
  (combinations || []).forEach((c) => {
    if (!byCount.has(c.flaggedCount)) byCount.set(c.flaggedCount, []);
    byCount.get(c.flaggedCount).push(c);
  });
  const groups = [...byCount.values()].map((members) => {
    const sorted = sortCombinationsByThreshold(members);
    const valuesByKey = {};
    sorted.forEach((c) =>
      keyedConditions(c.thresholds).forEach(({ key, threshold }) => {
        valuesByKey[key] = (valuesByKey[key] || new Set()).add(threshold.value);
      })
    );
    return {
      flaggedCount: sorted[0].flaggedCount,
      flaggedPct: sorted[0].flaggedPct,
      alertDeltaPct: sorted[0].alertDeltaPct,
      combinations: sorted,
      differs: Object.keys(valuesByKey).filter(
        (key) => valuesByKey[key].size > 1
      )
    };
  });
  const byAlerts = (a, b) => b.flaggedCount - a.flaggedCount;
  return groups.sort(
    targetAlertRate == null
      ? byAlerts
      : (a, b) =>
          Math.abs(a.flaggedPct - targetAlertRate) -
            Math.abs(b.flaggedPct - targetAlertRate) || byAlerts(a, b)
  );
};
